import React, { useState } from "react";
import {
  PaletteIcon,
  TypeIcon,
  SlidersHorizontalIcon,
  SquareIcon,
  MessageSquare,
  RefreshCw,
  Monitor,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { DEFAULT_DESIGN } from "./surveyDefaults";

const SurveyDesignCustomization = ({
  colorSettings,
  typographySettings,
  spacingSettings,
  sizeSettings,
  textSettings,
  presentationSettings,
  setColorSettings,
  setTypographySettings,
  setSpacingSettings,
  setSizeSettings,
  setTextSettings,
  setPresentationSettings,
  resetToDefaultDesign,
  isOpenEnded = false,
}) => {
  // ─── 1. Annotate these arrays so TS knows each element matches the union type ───
  const fontWeightOptions = ["normal", "medium", "semibold", "bold"];

  const shadowOptions = ["none", "soft", "default", "medium"];

  // presentationStyles needs to use PresentationSettings["style"] for id
  const presentationStyles = [
    {
      id: "center-modal",
      name: "Center Modal",
      description: "Traditional centered popup",
      icon: "⬜",
    },
    {
      id: "corner-float",
      name: "Corner Float",
      description: "Small floating widget in corner",
      icon: "🔘",
    },
    {
      id: "side-panel",
      name: "Side Panel",
      description: "Slide-in panel from edge",
      icon: "📱",
    },
  ];

  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("appearance");

  const handleResetToDefault = () => {
    if (resetToDefaultDesign) {
      resetToDefaultDesign();
    } else {
      setColorSettings(DEFAULT_DESIGN.colors);
      setTypographySettings(DEFAULT_DESIGN.typography);
      setSpacingSettings(DEFAULT_DESIGN.spacing);
      setSizeSettings(DEFAULT_DESIGN.size);
      setTextSettings(DEFAULT_DESIGN.text);
      setPresentationSettings(DEFAULT_DESIGN.presentation);
    }
  };

  const handleTypographySizeChange = (field, value) => {
    const num = parseInt(value);
    if (!isNaN(num)) {
      const max = MAX_VALUES[field] ?? 32;
      setTypographySettings({ ...typographySettings, [field]: Math.min(num, max) });
    }
  };

  const handleSpacingChange = (field, value) => {
    const num = parseInt(value);
    if (!isNaN(num)) {
      const max = MAX_VALUES[field] ?? 60;
      setSpacingSettings({ ...spacingSettings, [field]: Math.min(num, max) });
    }
  };

  const handleSizeChange = (field, value) => {
    // ─── 2. When updating shadowIntensity, cast the string to the correct union ───
    if (field === "shadowIntensity") {
      setSizeSettings({
        ...sizeSettings,
        shadowIntensity: value,
      });
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        const max = MAX_VALUES[field] ?? 800;
        setSizeSettings({
          ...sizeSettings,
          [field]: Math.min(num, max),
        });
      }
    }
  };

  const handleTextChange = (field, value) => {
    setTextSettings({ ...textSettings, [field]: value });
  };

  // max‐value lookups for sliders + number fields
  const MAX_VALUES = {
    // Typography
    titleSize: 32,
    optionSize: 24,
    buttonTextSize: 24,
    otherInputSize: 20,

    // Spacing
    popupPaddingY: 60,
    popupPaddingX: 60,
    titleBottomMargin: 48,
    optionSpacing: 32,
    buttonTopMargin: 48,

    // Size
    borderRadius: 30,
    borderWidth: 10,
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "appearance":
        return (
          <div className="px-2 py-4">
            {/* Colors Section */}
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <PaletteIcon className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Colors</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* popupBackground */}
                <div className="space-y-2">
                  <label htmlFor="popupBackground" className="text-xs font-medium text-gray-700">
                    Background
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colorSettings.popupBackground }}
                      onClick={() => document.getElementById("popupBackground")?.click()}
                    />
                    <input
                      id="popupBackground"
                      type="color"
                      value={colorSettings.popupBackground}
                      className="h-8 w-full p-1"
                      onChange={(e) =>
                        setColorSettings({ ...colorSettings, popupBackground: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* borderColor */}
                <div className="space-y-2">
                  <label htmlFor="borderColor" className="text-xs font-medium text-gray-700">
                    Border
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colorSettings.borderColor }}
                      onClick={() => document.getElementById("borderColor")?.click()}
                    />
                    <input
                      id="borderColor"
                      type="color"
                      value={colorSettings.borderColor}
                      className="h-8 w-full p-1"
                      onChange={(e) =>
                        setColorSettings({ ...colorSettings, borderColor: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* titleColor */}
                <div className="space-y-2">
                  <label htmlFor="titleColor" className="text-xs font-medium text-gray-700">
                    Title
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colorSettings.titleColor }}
                      onClick={() => document.getElementById("titleColor")?.click()}
                    />
                    <input
                      id="titleColor"
                      type="color"
                      value={colorSettings.titleColor}
                      className="h-8 w-full p-1"
                      onChange={(e) =>
                        setColorSettings({ ...colorSettings, titleColor: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* optionTextColor (only if not open-ended) */}
                {!isOpenEnded && (
                  <div className="space-y-2">
                    <label htmlFor="optionTextColor" className="text-xs font-medium text-gray-700">
                      Option Text
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                        style={{ backgroundColor: colorSettings.optionTextColor }}
                        onClick={() => document.getElementById("optionTextColor")?.click()}
                      />
                      <input
                        id="optionTextColor"
                        type="color"
                        value={colorSettings.optionTextColor}
                        className="h-8 w-full p-1"
                        onChange={(e) =>
                          setColorSettings({ ...colorSettings, optionTextColor: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* buttonBackground */}
                <div className="space-y-2">
                  <label htmlFor="buttonBackground" className="text-xs font-medium text-gray-700">
                    Button Background
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colorSettings.buttonBackground }}
                      onClick={() => document.getElementById("buttonBackground")?.click()}
                    />
                    <input
                      id="buttonBackground"
                      type="color"
                      value={colorSettings.buttonBackground}
                      className="h-8 w-full p-1"
                      onChange={(e) =>
                        setColorSettings({ ...colorSettings, buttonBackground: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* buttonTextColor */}
                <div className="space-y-2">
                  <label htmlFor="buttonTextColor" className="text-xs font-medium text-gray-700">
                    Button Text
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colorSettings.buttonTextColor }}
                      onClick={() => document.getElementById("buttonTextColor")?.click()}
                    />
                    <input
                      id="buttonTextColor"
                      type="color"
                      value={colorSettings.buttonTextColor}
                      className="h-8 w-full p-1"
                      onChange={(e) =>
                        setColorSettings({ ...colorSettings, buttonTextColor: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Typography Section */}
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <TypeIcon className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Typography</h4>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-700">Question Title</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Size (px)</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          value={typographySettings.titleSize}
                          min={14}
                          max={MAX_VALUES.titleSize}
                          step={1}
                          onChange={(e) =>
                            setTypographySettings({
                              ...typographySettings,
                              titleSize: parseInt(e.target.value),
                            })
                          }
                          className="flex-1"
                        />
                        <input
                          type="number"
                          className="w-16 h-8 text-xs text-center"
                          value={typographySettings.titleSize}
                          onChange={(e) => handleTypographySizeChange("titleSize", e.target.value)}
                          min={14}
                          max={MAX_VALUES.titleSize}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Weight</p>
                      <div className="flex gap-1">
                        {fontWeightOptions.map((weight) => (
                          <button
                            type="button"
                            key={weight}
                            className={`flex-1 rounded-md border px-2 py-1 text-xs ${typographySettings.titleWeight === weight
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-gray-200 text-gray-600 hover:bg-gray-50"
                              }`}
                            onClick={() =>
                              setTypographySettings({ ...typographySettings, titleWeight: weight })
                            }
                          >
                            {weight.charAt(0).toUpperCase() + weight.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {!isOpenEnded && (
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-700">Answer Options</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-gray-500">Size (px)</p>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            value={typographySettings.optionSize}
                            min={12}
                            max={MAX_VALUES.optionSize}
                            step={1}
                            onChange={(e) =>
                              setTypographySettings({
                                ...typographySettings,
                                optionSize: parseInt(e.target.value),
                              })
                            }
                            className="flex-1"
                          />
                          <input
                            type="number"
                            className="w-16 h-8 text-xs text-center"
                            value={typographySettings.optionSize}
                            onChange={(e) => handleTypographySizeChange("optionSize", e.target.value)}
                            min={12}
                            max={MAX_VALUES.optionSize}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-gray-500">Weight</p>
                        <div className="flex gap-1">
                          {fontWeightOptions.map((weight) => (
                            <button
                              type="button"
                              key={weight}
                              className={`flex-1 rounded-md border px-2 py-1 text-xs ${typographySettings.optionWeight === weight
                                ? "border-blue-500 bg-blue-50 text-blue-600"
                                : "border-gray-200 text-gray-600 hover:bg-gray-50"
                                }`}
                              onClick={() =>
                                setTypographySettings({ ...typographySettings, optionWeight: weight })
                              }
                            >
                              {weight.charAt(0).toUpperCase() + weight.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-700">Button Text</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Size (px)</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          value={typographySettings.buttonTextSize}
                          min={12}
                          max={MAX_VALUES.buttonTextSize}
                          step={1}
                          onChange={(e) =>
                            setTypographySettings({
                              ...typographySettings,
                              buttonTextSize: parseInt(e.target.value),
                            })
                          }
                          className="flex-1"
                        />
                        <input
                          type="number"
                          className="w-16 h-8 text-xs text-center"
                          value={typographySettings.buttonTextSize}
                          onChange={(e) => handleTypographySizeChange("buttonTextSize", e.target.value)}
                          min={12}
                          max={MAX_VALUES.buttonTextSize}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Weight</p>
                      <div className="flex gap-1">
                        {fontWeightOptions.map((weight) => (
                          <button
                            type="button"
                            key={weight}
                            className={`flex-1 rounded-md border px-2 py-1 text-xs ${typographySettings.buttonTextWeight === weight
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-gray-200 text-gray-600 hover:bg-gray-50"
                              }`}
                            onClick={() =>
                              setTypographySettings({ ...typographySettings, buttonTextWeight: weight })
                            }
                          >
                            {weight.charAt(0).toUpperCase() + weight.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Spacing & Layout */}
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <SlidersHorizontalIcon className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Spacing & Layout</h4>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Popup Padding</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Vertical (py)</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          value={spacingSettings.popupPaddingY}
                          min={0}
                          max={MAX_VALUES.popupPaddingY}
                          step={4}
                          onChange={(e) =>
                            setSpacingSettings({
                              ...spacingSettings,
                              popupPaddingY: parseInt(e.target.value),
                            })
                          }
                          className="flex-1"
                        />
                        <input
                          type="number"
                          className="w-16 h-8 text-xs text-center"
                          value={spacingSettings.popupPaddingY}
                          onChange={(e) => handleSpacingChange("popupPaddingY", e.target.value)}
                          min={0}
                          max={MAX_VALUES.popupPaddingY}
                          step={4}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Horizontal (px)</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          value={spacingSettings.popupPaddingX}
                          min={0}
                          max={MAX_VALUES.popupPaddingX}
                          step={4}
                          onChange={(e) =>
                            setSpacingSettings({
                              ...spacingSettings,
                              popupPaddingX: parseInt(e.target.value),
                            })
                          }
                          className="flex-1"
                        />
                        <input
                          type="number"
                          className="w-16 h-8 text-xs text-center"
                          value={spacingSettings.popupPaddingX}
                          onChange={(e) => handleSpacingChange("popupPaddingX", e.target.value)}
                          min={0}
                          max={MAX_VALUES.popupPaddingX}
                          step={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    {isOpenEnded ? "Title to Text Box Margin (px)" : "Title to Options Margin (px)"}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      value={spacingSettings.titleBottomMargin}
                      min={0}
                      max={MAX_VALUES.titleBottomMargin}
                      step={4}
                      onChange={(e) =>
                        setSpacingSettings({
                          ...spacingSettings,
                          titleBottomMargin: parseInt(e.target.value),
                        })
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      className="w-16 h-8 text-xs text-center"
                      value={spacingSettings.titleBottomMargin}
                      onChange={(e) => handleSpacingChange("titleBottomMargin", e.target.value)}
                      min={0}
                      max={MAX_VALUES.titleBottomMargin}
                      step={4}
                    />
                  </div>
                </div>

                {!isOpenEnded && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Space Between Options (px)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        value={spacingSettings.optionSpacing}
                        min={2}
                        max={MAX_VALUES.optionSpacing}
                        step={1}
                        onChange={(e) =>
                          setSpacingSettings({
                            ...spacingSettings,
                            optionSpacing: parseInt(e.target.value),
                          })
                        }
                        className="flex-1"
                      />
                      <input
                        type="number"
                        className="w-16 h-8 text-xs text-center"
                        value={spacingSettings.optionSpacing}
                        onChange={(e) => handleSpacingChange("optionSpacing", e.target.value)}
                        min={2}
                        max={MAX_VALUES.optionSpacing}
                        step={1}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Button Top Margin (px)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      value={spacingSettings.buttonTopMargin}
                      min={2}
                      max={MAX_VALUES.buttonTopMargin}
                      step={4}
                      onChange={(e) =>
                        setSpacingSettings({
                          ...spacingSettings,
                          buttonTopMargin: parseInt(e.target.value),
                        })
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      className="w-16 h-8 text-xs text-center"
                      value={spacingSettings.buttonTopMargin}
                      onChange={(e) => handleSpacingChange("buttonTopMargin", e.target.value)}
                      min={2}
                      max={MAX_VALUES.buttonTopMargin}
                      step={4}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Size & Shape */}
            <div className="mb-4">
              <div className="mb-4 flex items-center gap-2">
                <SquareIcon className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Size & Shape</h4>
              </div>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Border Radius (px)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      value={sizeSettings.borderRadius}
                      min={16}
                      max={MAX_VALUES.borderRadius}
                      step={1}
                      onChange={(e) =>
                        setSizeSettings({ ...sizeSettings, borderRadius: parseInt(e.target.value) })
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      className="w-16 h-8 text-xs text-center"
                      value={sizeSettings.borderRadius}
                      onChange={(e) => handleSizeChange("borderRadius", e.target.value)}
                      min={16}
                      max={MAX_VALUES.borderRadius}
                      step={1}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Border Width (px)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      value={sizeSettings.borderWidth}
                      min={1}
                      max={MAX_VALUES.borderWidth}
                      step={1}
                      onChange={(e) =>
                        setSizeSettings({ ...sizeSettings, borderWidth: parseInt(e.target.value) })
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      className="w-16 h-8 text-xs text-center"
                      value={sizeSettings.borderWidth}
                      onChange={(e) => handleSizeChange("borderWidth", e.target.value)}
                      min={1}
                      max={MAX_VALUES.borderWidth}
                      step={1}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-700">Shadow Intensity</label>
                  <div className="flex gap-2">
                    {shadowOptions.map((intensity) => (
                      <button
                        type="button"
                        key={intensity}
                        className={`flex-1 rounded-md border px-2 py-1.5 text-xs ${sizeSettings.shadowIntensity === intensity
                          ? "border-blue-500 bg-blue-50 text-blue-600"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                        onClick={() =>
                          setSizeSettings({ ...sizeSettings, shadowIntensity: intensity })
                        }
                      >
                        {intensity.charAt(0).toUpperCase() + intensity.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "text":
        return (
          <div className="px-2 py-4">
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Survey Texts</h4>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="submitButtonText" className="text-xs font-medium text-gray-700">
                    Submit Button Text
                  </label>
                  <input
                    id="submitButtonText"
                    value=""
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    onChange={(e) => handleTextChange("submitButtonText", e.target.value)}
                    placeholder="Share Response"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="assistantTitle" className="text-xs font-medium text-gray-700">
                    Chat Assistant Title
                  </label>
                  <input
                    id="assistantTitle"
                    value=""
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    onChange={(e) => handleTextChange("assistantTitle", e.target.value)}
                    placeholder="AI Product Assistant"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="inputPlaceholder" className="text-xs font-medium text-gray-700">
                    Chat Input Placeholder
                  </label>
                  <input
                    id="inputPlaceholder"
                    value=""
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    onChange={(e) => handleTextChange("inputPlaceholder", e.target.value)}
                    placeholder="Type your answer..."
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="chatButtonText" className="text-xs font-medium text-gray-700">
                    Chat Button Text (Closed State)
                  </label>
                  <input
                    id="chatButtonText"
                    value=""
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    onChange={(e) => handleTextChange("chatButtonText", e.target.value)}
                    placeholder="Need help? Chat with us"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "chat":
        return (
          <div className="px-2 py-4">
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Chat Message Colors</h4>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-700">AI Messages</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="aiMessageBackground"
                        className="text-xs font-medium text-gray-700"
                      >
                        Background
                      </label>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                          style={{ backgroundColor: colorSettings.aiMessageBackground }}
                          onClick={() => document.getElementById("aiMessageBackground")?.click()}
                        />
                        <input
                          id="aiMessageBackground"
                          type="color"
                          value={colorSettings.aiMessageBackground}
                          className="h-8 w-full p-1"
                          onChange={(e) =>
                            setColorSettings({ ...colorSettings, aiMessageBackground: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="aiMessageText" className="text-xs font-medium text-gray-700">
                        Text
                      </label>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                          style={{ backgroundColor: colorSettings.aiMessageText }}
                          onClick={() => document.getElementById("aiMessageText")?.click()}
                        />
                        <input
                          id="aiMessageText"
                          type="color"
                          value={colorSettings.aiMessageText}
                          className="h-8 w-full p-1"
                          onChange={(e) =>
                            setColorSettings({ ...colorSettings, aiMessageText: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-xs font-medium text-gray-700">
                    Visitor Messages
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="visitorMessageBackground"
                        className="text-xs font-medium text-gray-700"
                      >
                        Background
                      </label>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                          style={{ backgroundColor: colorSettings.visitorMessageBackground }}
                          onClick={() => document.getElementById("visitorMessageBackground")?.click()}
                        />
                        <input
                          id="visitorMessageBackground"
                          type="color"
                          value={colorSettings.visitorMessageBackground}
                          className="h-8 w-full p-1"
                          onChange={(e) =>
                            setColorSettings({
                              ...colorSettings,
                              visitorMessageBackground: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="visitorMessageText"
                        className="text-xs font-medium text-gray-700"
                      >
                        Text
                      </label>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-5 w-5 rounded border shadow-sm cursor-pointer"
                          style={{ backgroundColor: colorSettings.visitorMessageText }}
                          onClick={() => document.getElementById("visitorMessageText")?.click()}
                        />
                        <input
                          id="visitorMessageText"
                          type="color"
                          value={colorSettings.visitorMessageText}
                          className="h-8 w-full p-1"
                          onChange={(e) =>
                            setColorSettings({ ...colorSettings, visitorMessageText: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 mt-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="mb-3">
                    <div
                      className="p-3 mb-2 rounded-lg max-w-[80%]"
                      style={{
                        backgroundColor: colorSettings.aiMessageBackground,
                        color: colorSettings.aiMessageText,
                      }}
                    >
                      <p className="text-sm">This is how an AI message will look in the chat.</p>
                    </div>
                    <div
                      className="p-3 rounded-lg ml-auto max-w-[80%]"
                      style={{
                        backgroundColor: colorSettings.visitorMessageBackground,
                        color: colorSettings.visitorMessageText,
                      }}
                    >
                      <p className="text-sm">This is how a visitor message will look.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "presentation":
        return (
          <div className="px-2 py-4">
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <Monitor className="h-5 w-5 text-gray-700" />
                <h4 className="font-medium">Presentation Style</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Choose how your survey appears to visitors on your website.
              </p>

              {/* Style Selection Grid */}
              <div className="grid grid-cols-1 gap-3 mb-6">
                {presentationStyles.map((style) => (
                  <button
                    type="button"
                    key={style.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${presentationSettings.style === style.id
                      ? "border-blue-500 bg-blue-50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    onClick={() =>
                      setPresentationSettings({ ...presentationSettings, style: style.id })
                    }
                  >
                    <div className="text-2xl">{style.icon}</div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{style.name}</div>
                      <div className="text-xs text-gray-600">{style.description}</div>
                    </div>
                    {presentationSettings.style === style.id && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>

              {/* Style-specific options */}
              {presentationSettings.style === "corner-float" && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-sm">Corner Position</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`p-2 text-xs rounded border ${presentationSettings.cornerPosition === "bottom-left"
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-gray-200 hover:bg-gray-100"
                        }`}
                      onClick={() =>
                        setPresentationSettings({ ...presentationSettings, cornerPosition: "bottom-left" })
                      }
                    >
                      Bottom Left
                    </button>
                    <button
                      type="button"
                      className={`p-2 text-xs rounded border ${presentationSettings.cornerPosition === "bottom-right"
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-gray-200 hover:bg-gray-100"
                        }`}
                      onClick={() =>
                        setPresentationSettings({ ...presentationSettings, cornerPosition: "bottom-right" })
                      }
                    >
                      Bottom Right
                    </button>
                  </div>
                </div>
              )}

              {presentationSettings.style === "side-panel" && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-sm">Slide Direction</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`p-2 text-xs rounded border ${presentationSettings.slideDirection === "left"
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-gray-200 hover:bg-gray-100"
                        }`}
                      onClick={() =>
                        setPresentationSettings({ ...presentationSettings, slideDirection: "left" })
                      }
                    >
                      From Left
                    </button>
                    <button
                      type="button"
                      className={`p-2 text-xs rounded border ${presentationSettings.slideDirection === "right"
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-gray-200 hover:bg-gray-100"
                        }`}
                      onClick={() =>
                        setPresentationSettings({ ...presentationSettings, slideDirection: "right" })
                      }
                    >
                      From Right
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`rounded-lg border bg-white shadow-sm ${!isOpen ? "mb-4" : ""}`}>
      <div className="flex items-center justify-between p-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">Customize Survey</h3>
            <span className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-normal">
              New
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Match the survey to your store's branding. All changes update the live preview in real time.
          </p>
        </div>
        <div className="flex items-center ml-4 gap-2">
          <button
            type="button"
            onClick={handleResetToDefault}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm"
            title="Reset to default design"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="font-medium w-[82px]">Reset Design</span>
          </button>
          <button type="button" className="rounded-md p-2 hover:bg-gray-100" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div>
          <div className="border-t px-4 py-2">
            {/* Tab navigation */}
            <div className="flex border-b">
              <button
                type="button"
                className={`px-4 py-2 ${activeTab === "appearance"
                  ? "border-b-2 border-blue-500 text-blue-600 font-medium"
                  : "text-gray-600"
                  }`}
                onClick={() => setActiveTab("appearance")}
              >
                Appearance
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${activeTab === "text" ? "border-b-2 border-blue-500 text-blue-600 font-medium" : "text-gray-600"
                  }`}
                onClick={() => setActiveTab("text")}
              >
                Text Content
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${activeTab === "chat" ? "border-b-2 border-blue-500 text-blue-600 font-medium" : "text-gray-600"
                  }`}
                onClick={() => setActiveTab("chat")}
              >
                Chat Style
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${activeTab === "presentation"
                    ? "border-b-2 border-blue-500 text-blue-600 font-medium"
                    : "text-gray-600"
                  }`}
                onClick={() => setActiveTab("presentation")}
              >
                Presentation
              </button>
            </div>

            {renderTabContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyDesignCustomization;
